import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import _ from 'lodash'
import { Box } from '@mui/material'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import Dropzone from 'react-dropzone'
import Loading from '../../../process-state-notifications/Loading'
import {
  getMarkdownFromSourceEditor,
  getMarkdownSourceExtensions,
} from '../../richtext'
import {
  applyMarkdownSyntax,
  findHighlightItemsInMarkdownSource,
  insertMarkdownImages,
  insertMarkdownTable,
} from './markdownSyntax'
import { RichTextStyleButtons } from '../../../toolbars/RichTextToolbar'
import { StyleType } from '../../../../../utils/markdown'
import { UploadedFile } from '../../../../../utils/file'

type MarkdownEditViewProps = {
  loading: boolean
  toolbarSlot: HTMLElement | null
  onDropAccepted: (acceptedFiles: File[]) => void
  onEditorReady: (editor: Editor) => void
  onEditorDestroy: () => void
  onContentChange: (markdown: string) => void
}

export const MarkdownEditView = ({
  loading,
  toolbarSlot,
  onDropAccepted,
  onEditorReady,
  onEditorDestroy,
  onContentChange,
}: MarkdownEditViewProps) => {
  const [highlightItems, setHighlightItems] = useState<string[]>([])

  // useEditor の callback は生成時にキャプチャされるため、
  // 最新の callback 参照を ref 経由で読み直して stale 状態を避ける。
  const onContentChangeRef = useRef(onContentChange)
  onContentChangeRef.current = onContentChange

  const debouncedFindHighlight = useMemo(
    () =>
      _.debounce((ed: Editor) => {
        setHighlightItems(findHighlightItemsInMarkdownSource(ed))
      }, 100),
    []
  )

  const editor = useEditor({
    extensions: getMarkdownSourceExtensions(),
    content: '',
    editorProps: {
      attributes: {
        class: 'markdown-source-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onContentChangeRef.current(getMarkdownFromSourceEditor(ed))
      debouncedFindHighlight(ed)
    },
    onSelectionUpdate: ({ editor: ed }) => {
      debouncedFindHighlight(ed)
    },
  })

  useEffect(() => {
    if (!editor) return
    onEditorReady(editor)
    return () => {
      onEditorDestroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor参照の登録/解除だけ
  }, [editor])

  const handleStyleApply = useCallback(
    (styleType: StyleType) => {
      if (!editor) return
      applyMarkdownSyntax(editor, styleType)
    },
    [editor]
  )

  const handleTableInsert = useCallback(
    (rows: number, columns: number) => {
      if (!editor) return
      insertMarkdownTable(editor, rows, columns)
    },
    [editor]
  )

  const handleFileInsert = useCallback(
    (files: UploadedFile[]) => {
      if (!editor) return
      insertMarkdownImages(editor, files)
    },
    [editor]
  )

  return (
    <>
      {toolbarSlot &&
        createPortal(
          <RichTextStyleButtons
            editor={editor ?? undefined}
            hideHeaderFour={true}
            hideHeaderFive={true}
            hideHeaderSix={true}
            highlightItems={highlightItems}
            onStyleApply={handleStyleApply}
            onTableInsert={handleTableInsert}
            onFileInsert={handleFileInsert}
          />,
          toolbarSlot
        )}
      <Loading isLoading={loading} />
      <Dropzone onDropAccepted={onDropAccepted} multiple={true}>
        {({ getRootProps, getInputProps }) => (
          <Box
            {...getRootProps({
              onClick: e => {
                e.stopPropagation()
              },
            })}
            sx={{
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
            }}
          >
            <input {...getInputProps()} />
            <EditorContent
              editor={editor}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )}
      </Dropzone>
    </>
  )
}
